import { notify } from '@/components/core';
import React, { useCallback, useRef, useState } from 'react';
import { getStoredToken, apiFetch } from '../../api';

import ReactFlow, {
    Controls,
    useReactFlow,
    useOnSelectionChange,
    type Edge,
    type Node,
} from 'reactflow';
import 'reactflow/dist/style.css';
import SmartTransformNode from './nodes/SmartTransformNode';
import { DataProfileNode } from './nodes/DataProfileNode';
import SourceNode from './nodes/SourceNode';
import FilterNode from './nodes/FilterNode';
import DestinationNode from './nodes/DestinationNode';
import { memo } from 'react';
import { BaseNodeUI } from './nodes/BaseNodeUI';
import { FileOutput, Sigma, Layers, TableProperties, FunctionSquare, LayoutGrid, LayoutList, ScanSearch, Database, BrainCircuit, Zap, BarChart, Cpu, ArrowDownWideNarrow, CopyMinus, Box, TrendingUp, PieChart, Trophy, History, Wand2, Mail, Send, FileSearch, UserCheck, Calendar, Play, FilePen, Package, LogOut } from 'lucide-react';
import JoinNode from './nodes/JoinNode';
import ConditionalSplitNode from './nodes/ConditionalSplitNode';
import { AICommandBar } from './AICommandBar';
import EmailAttributeFilterNode from './nodes/EmailAttributeFilterNode';
import EmailPatternFilterNode from './nodes/EmailPatternFilterNode';
import AttachmentFilterNode from './nodes/AttachmentFilterNode';
import AttachmentProcessorNode from './nodes/AttachmentProcessorNode';

const TrainModelNode = memo(({ data, selected }: any) => (
    <BaseNodeUI title="Train Model" icon={BrainCircuit} colorClass="" bgClass="bg-gradient-to-br from-violet-500 to-violet-700" selected={selected} isRunning={data?.executionStatus === 'Running'}>
        ML Training
    </BaseNodeUI>
));

const TrainedModelNode = memo(({ data, selected }: any) => (
    <BaseNodeUI title="Trained Model" icon={Package} colorClass="" bgClass="bg-gradient-to-br from-emerald-500 to-emerald-700" selected={selected} isRunning={data?.executionStatus === 'Running'}>
        Model Registry
    </BaseNodeUI>
));

const EmailTriggerNode = memo(({ data, selected }: any) => (
    <BaseNodeUI title="Email Source" icon={Mail} colorClass="" bgClass="bg-gradient-to-br from-blue-400 to-blue-600" selected={selected} isRunning={data?.executionStatus === 'Running'} isSource={true}>
        Attachment
    </BaseNodeUI>
));

const EmailActionNode = memo(({ data, selected }: any) => (
    <BaseNodeUI
        title="Send Email"
        icon={Send}
        colorClass=""
        bgClass="bg-gradient-to-br from-green-500 to-green-700"
        selected={selected}
        executionStatus={data?.executionStatus}
        isSource={false}
    >
        Notification
    </BaseNodeUI>
));



// ... (existing code)

const AggregateNode = memo(({ data, selected }: any) => (
    <BaseNodeUI title={data.label || "Aggregate"} icon={Sigma} colorClass="" bgClass="bg-gradient-to-br from-purple-500 to-purple-700" selected={selected} isRunning={data?.executionStatus === 'Running'} executionStatus={data?.executionStatus}>
        Sum/Count
    </BaseNodeUI>
));
const FormulaNode = memo(({ data, selected }: any) => (
    <BaseNodeUI title={data.label || "Formula"} icon={FunctionSquare} colorClass="" bgClass="bg-gradient-to-br from-blue-500 to-blue-700" selected={selected} isRunning={data?.executionStatus === 'Running'} executionStatus={data?.executionStatus}>
        f(x)
    </BaseNodeUI>
));
const OutputNode = memo(({ data, selected }: any) => (
    <BaseNodeUI title={data.label || 'Save to Data Model'} icon={FileOutput} colorClass="" bgClass="bg-gradient-to-br from-teal-500 to-teal-700" selected={selected} isRunning={data?.executionStatus === 'Running'} executionStatus={data?.executionStatus} isSource={false}>
        Data Model
    </BaseNodeUI>
));

const CleanNode = memo(({ data, selected }: any) => (
    <BaseNodeUI title="Cleanse" icon={Layers} colorClass="" bgClass="bg-gradient-to-br from-orange-500 to-orange-700" selected={selected} isRunning={data?.executionStatus === 'Running'} executionStatus={data?.executionStatus}>
        Data Prep
    </BaseNodeUI>
));

const ColumnNode = memo(({ data, selected }: any) => (
    <BaseNodeUI title={data.label || "Columns"} icon={TableProperties} colorClass="" bgClass="bg-gradient-to-br from-yellow-500 to-yellow-700" selected={selected} isRunning={data?.executionStatus === 'Running'} executionStatus={data?.executionStatus}>
        Mapping
    </BaseNodeUI>
));

const PivotNode = memo(({ data, selected }: any) => (
    <BaseNodeUI title={data.label || "Pivot"} icon={LayoutGrid} colorClass="" bgClass="bg-gradient-to-br from-indigo-500 to-indigo-700" selected={selected} isRunning={data?.executionStatus === 'Running'} executionStatus={data?.executionStatus}>
        Cross Tab
    </BaseNodeUI>
));

const UnpivotNode = memo(({ data, selected }: any) => (
    <BaseNodeUI title={data.label || "Unpivot"} icon={LayoutList} colorClass="" bgClass="bg-gradient-to-br from-indigo-500 to-indigo-700" selected={selected} isRunning={data?.executionStatus === 'Running'} executionStatus={data?.executionStatus}>
        Normalize
    </BaseNodeUI>
));

const DeduplicateNode = memo(({ data, selected }: any) => (
    <BaseNodeUI title="Deduplicate" icon={CopyMinus} colorClass="" bgClass="bg-gradient-to-br from-orange-500 to-orange-700" selected={selected} isRunning={data?.executionStatus === 'Running'} executionStatus={data?.executionStatus}>
        Unique
    </BaseNodeUI>
));

const SortNode = memo(({ data, selected }: any) => (
    <BaseNodeUI title="Sort" icon={ArrowDownWideNarrow} colorClass="" bgClass="bg-gradient-to-br from-teal-500 to-teal-700" selected={selected} isRunning={data?.executionStatus === 'Running'} executionStatus={data?.executionStatus}>
        Order Data
    </BaseNodeUI>
));

const AutoTypeNode = memo(({ data, selected }: any) => (
    <BaseNodeUI title={data.label || "Auto Type"} icon={ScanSearch} colorClass="" bgClass="bg-gradient-to-br from-amber-500 to-amber-700" selected={selected} isRunning={data?.executionStatus === 'Running'} executionStatus={data?.executionStatus}>
        Smart Cast
    </BaseNodeUI>
));

const UnionNode = memo(({ data, selected }: any) => (
    <BaseNodeUI title="Union" icon={Layers} colorClass="" bgClass="bg-gradient-to-br from-purple-500 to-purple-700" selected={selected} isRunning={data?.executionStatus === 'Running'} executionStatus={data?.executionStatus}>
        Append
    </BaseNodeUI>
));

const ExecuteSqlNode = memo(({ data, selected }: any) => (
    <BaseNodeUI title={data.label || "Execute SQL"} icon={Database} colorClass="" bgClass="bg-gradient-to-br from-slate-600 to-slate-800" selected={selected} isRunning={data?.executionStatus === 'Running'} executionStatus={data?.executionStatus}>
        Command (Vars)
    </BaseNodeUI>
));


const SqlDmlNode = memo(({ data, selected }: any) => (
    <BaseNodeUI title={data.label || "SQL DML"} icon={FilePen} colorClass="" bgClass="bg-gradient-to-br from-slate-600 to-slate-800" selected={selected} isRunning={data?.executionStatus === 'Running'} executionStatus={data?.executionStatus} isSource={false}>
        DML Query
    </BaseNodeUI>
));


// ML Nodes


const ScoreModelNode = memo(({ data, selected }: any) => (
    <BaseNodeUI title="Score Model" icon={Zap} colorClass="" bgClass="bg-gradient-to-br from-amber-500 to-amber-700" selected={selected} isRunning={data?.executionStatus === 'Running'} executionStatus={data?.executionStatus}>
        Prediction
    </BaseNodeUI>
));

const PredictModelNode = memo(({ data, selected }: any) => (
    <BaseNodeUI title="Predict" icon={Zap} colorClass="" bgClass="bg-gradient-to-br from-amber-500 to-amber-700" selected={selected} isRunning={data?.executionStatus === 'Running'} executionStatus={data?.executionStatus}>
        Apply Model
    </BaseNodeUI>
));

const ParetoNode = memo(({ data, selected }: any) => (
    <BaseNodeUI title="Pareto" icon={Trophy} colorClass="" bgClass="bg-gradient-to-br from-orange-500 to-orange-700" selected={selected} isRunning={data?.executionStatus === 'Running'} executionStatus={data?.executionStatus}>
        80/20 Rule
    </BaseNodeUI>
));

const WindowNode = memo(({ data, selected }: any) => (
    <BaseNodeUI title="Window" icon={History} colorClass="" bgClass="bg-gradient-to-br from-indigo-500 to-indigo-700" selected={selected} isRunning={data?.executionStatus === 'Running'} executionStatus={data?.executionStatus}>
        Rolling Stats
    </BaseNodeUI>
));

const EvaluateModelNode = memo(({ data, selected }: any) => (
    <BaseNodeUI title="Evaluate" icon={BarChart} colorClass="" bgClass="bg-gradient-to-br from-cyan-500 to-cyan-700" selected={selected} isRunning={data?.executionStatus === 'Running'} executionStatus={data?.executionStatus}>
        Metrics
    </BaseNodeUI>
));

const AiInsightNode = memo(({ data, selected }: any) => (
    <BaseNodeUI title="Agentic Insight" icon={Cpu} colorClass="" bgClass="bg-gradient-to-br from-indigo-500 to-indigo-700" selected={selected} isRunning={data?.executionStatus === 'Running'} executionStatus={data?.executionStatus}>
        Analysis
    </BaseNodeUI>
));

const MdmIngestNode = memo(({ data, selected }: any) => (
    <BaseNodeUI title="MDM Ingest" icon={Database} colorClass="" bgClass="bg-gradient-to-br from-indigo-600 to-indigo-800" selected={selected} isRunning={data?.executionStatus === 'Running'} executionStatus={data?.executionStatus} isSource={false}>
        Master Data
    </BaseNodeUI>
));

const SmartImputeNode = memo(({ data, selected }: any) => (
    <BaseNodeUI title="Smart Impute" icon={Wand2} colorClass="" bgClass="bg-gradient-to-br from-indigo-500 to-purple-600" selected={selected} isRunning={data?.executionStatus === 'Running'} executionStatus={data?.executionStatus}>
        Smart Fill
    </BaseNodeUI>
));

const DataModelNode = memo(({ data, selected }: any) => (
    <BaseNodeUI title={data.label || "Data Model"} icon={Database} colorClass="" bgClass="bg-gradient-to-br from-blue-600 to-indigo-700" selected={selected} isRunning={data?.executionStatus === 'Running'} executionStatus={data?.executionStatus}>
        Schema Definition
    </BaseNodeUI>
));

const ScheduleNode = memo(({ data, selected }: any) => (
    <BaseNodeUI title="Schedule" icon={Calendar} colorClass="" bgClass="bg-gradient-to-br from-slate-500 to-slate-700" selected={selected} isRunning={data?.executionStatus === 'Running'} executionStatus={data?.executionStatus} isSource={true}>
        Cron / Time
    </BaseNodeUI>
));

interface FlowCanvasProps {
    onSelectionChange?: (node: Node | null) => void;
    nodes: Node[];
    edges: Edge[];
    onNodesChange: any;
    onEdgesChange: any;
    onConnect: any;
    setNodes?: any;
    setEdges?: any;
    onPaneClick?: () => void;
    onNodeClick?: (event: React.MouseEvent, node: Node) => void;
    onRunNode?: (nodeId: string) => void;
    readOnly?: boolean;          // Published flows: view-only, no canvas edits allowed
    onLockToggle?: () => void;   // Called to unpublish (when locked) or open publish modal (when unlocked)
    isTogglingLock?: boolean;    // Shows spinner on lock button while API call is in progress
}

import SalesforceNode from './nodes/SalesforceNode';
import AnnotationNode from './nodes/AnnotationNode';

import ContainerNode from './nodes/ContainerNode';

import VisualNode from './nodes/VisualNode';
import { DateNode } from './nodes/DateNode';
import GeoNode from './nodes/GeoNode';
import CurrencyNode from './nodes/CurrencyNode';
import EdiSourceNode from './nodes/EdiSourceNode';
import RemoteFileNode from './nodes/RemoteFileNode';
import { ApprovalNode } from './nodes/ApprovalNode';
import { FetchDocumentNode } from './nodes/FetchDocumentNode';
import SignalStartNode from './nodes/SignalStartNode';
import { UniversalApiNode } from './nodes/UniversalApiNode';
import { AIAgentNode } from './nodes/AIAgentNode';
import FlowRunnerNode from './nodes/FlowRunnerNode';
import EmailListenerTriggerNode from './nodes/EmailListenerTriggerNode';
import ForEachNode from './nodes/ForEachNode';
import BarrierNode from './nodes/BarrierNode';
import MultiJoinNode from './nodes/MultiJoinNode';
import TeamsNode from './nodes/TeamsNode';
import { UserActionNode } from './nodes/UserActionNode';
import WebhookNode from './nodes/WebhookNode';
import GenerateDocumentNode from './nodes/GenerateDocumentNode';
import FileListenerNode from './nodes/FileListenerNode';
import DocumentParserNode from './nodes/DocumentParserNode';
import ConditionalRouterNode from './nodes/ConditionalRouterNode';
import DataMapperNode from './nodes/DataMapperNode';
import DocumentGeneratorNode from './nodes/DocumentGeneratorNode';
import DuplicateDetectorNode from './nodes/DuplicateDetectorNode';
import PythonScriptNode from './nodes/PythonScriptNode';
import LoopNode from './nodes/LoopNode';
import ContentMergerNode from './nodes/ContentMergerNode';
import AISummarizerNode from './nodes/AISummarizerNode';
import DatabaseLookupNode from './nodes/DatabaseLookupNode';
import TemplateRendererNode from './nodes/TemplateRendererNode';
import FileOutputNode from './nodes/FileOutputNode';
import LLMPromptNode from './nodes/LLMPromptNode';
import RelaySourceNode from './nodes/RelaySourceNode';
import RelaySinkNode from './nodes/RelaySinkNode';
import UserInputNode from './nodes/UserInputNode';
import FileSourceNode from './nodes/FileSourceNode';
import HubSpotNode from './nodes/HubSpotNode';
import FileObjectNode from './nodes/FileObjectNode';
import HybridFileHandlerNode from './nodes/HybridFileHandlerNode';


const SharePointAccessNode = memo(({ data, selected }: any) => (
    <BaseNodeUI title="SharePoint Access" icon={Database} bgClass="bg-gradient-to-br from-indigo-400 to-indigo-600" colorClass="" selected={selected} isRunning={data?.executionStatus === 'Running'}>
        Storage
    </BaseNodeUI>
));

const SharePointSearchNode = memo(({ data, selected }: any) => (
    <BaseNodeUI title="SharePoint Search" icon={FileSearch} bgClass="bg-gradient-to-br from-indigo-400 to-indigo-600" colorClass="" selected={selected} isRunning={data?.executionStatus === 'Running'} isSource={true}>
        Search
    </BaseNodeUI>
));


const nodeTypes = {
    remoteFile: RemoteFileNode,
    REMOTE_FILE: RemoteFileNode,

    approval: ApprovalNode,
    APPROVAL: ApprovalNode,

    fetchDocument: FetchDocumentNode,
    FETCH_DOCUMENT: FetchDocumentNode,

    aiTransform: SmartTransformNode,
    AI_TRANSFORM: SmartTransformNode,

    dataProfile: DataProfileNode,
    DATA_PROFILE: DataProfileNode,

    source: SourceNode,
    SOURCE: SourceNode,

    ediSource: EdiSourceNode,
    EDI_PARSE: EdiSourceNode, // Assuming EDI_PARSE maps to EdiSource

    filter: FilterNode,
    FILTER: FilterNode,

    join: JoinNode,
    JOIN: JoinNode,

    aggregate: AggregateNode,
    AGGREGATE: AggregateNode,

    formula: FormulaNode,
    FORMULA: FormulaNode,

    output: OutputNode,
    // SINK might map to DestinationNode or OutputNode. PropertiesPanel maps SINK to destination.
    // However, existing output is mapped to OutputNode.
    // Let's assume SINK -> DestinationNode based on PropertiesPanel change.

    clean: CleanNode,
    CLEAN: CleanNode,

    smartImpute: SmartImputeNode,
    SMART_IMPUTE: SmartImputeNode,

    column: ColumnNode,
    COLUMN: ColumnNode,

    pivot: PivotNode,
    PIVOT: PivotNode,

    signalStart: SignalStartNode,
    SIGNAL_START: SignalStartNode,
    SIGNALSTART: SignalStartNode,

    unpivot: UnpivotNode,
    UNPIVOT: UnpivotNode,

    autoType: AutoTypeNode,
    AUTO_TYPE: AutoTypeNode,

    union: UnionNode,
    UNION: UnionNode,

    executeSql: ExecuteSqlNode,
    EXECUTE_SQL: ExecuteSqlNode,
    sqlDml: SqlDmlNode,
    SQL_DML: SqlDmlNode,

    conditionalSplit: ConditionalSplitNode,
    CONDITIONAL_SPLIT: ConditionalSplitNode,
    split: ConditionalSplitNode,
    SPLIT: ConditionalSplitNode,
    COND_SPLIT: ConditionalSplitNode,
    cond_split: ConditionalSplitNode,

    destination: DestinationNode,
    SINK: DestinationNode, // Mapping SINK to DestinationNode

    salesforceDestination: SalesforceNode,

    trainModel: TrainModelNode,
    ML_TRAIN: TrainModelNode,

    trainedModel: TrainedModelNode,
    ML_TRAINED_MODEL: TrainedModelNode,

    scoreModel: ScoreModelNode,
    ML_SCORE: ScoreModelNode,

    mlPredict: PredictModelNode,
    predict: PredictModelNode,
    ML_PREDICT: PredictModelNode,

    pareto: ParetoNode,
    PARETO: ParetoNode,

    window: WindowNode,
    WINDOW: WindowNode,

    evaluateModel: EvaluateModelNode,
    ML_EVALUATE: EvaluateModelNode,

    sort: SortNode,
    SORT: SortNode,

    deduplicate: DeduplicateNode,
    DEDUPLICATE: DeduplicateNode,

    aiInsight: AiInsightNode,
    AI_INSIGHT: AiInsightNode,
    llmNode: LLMPromptNode,
    LLM_NODE: LLMPromptNode,

    mdmIngest: MdmIngestNode,
    MDM_INGEST: MdmIngestNode,

    annotation: AnnotationNode,
    ANNOTATION: AnnotationNode,

    container: ContainerNode,
    CONTAINER: ContainerNode,

    emailTrigger: EmailTriggerNode,
    EMAIL_TRIGGER: EmailTriggerNode,
    EMAILTRIGGER: EmailTriggerNode,
    fetchemail: EmailTriggerNode,

    eventListenerEmail: EmailListenerTriggerNode,
    EVENT_LISTENER_EMAIL: EmailListenerTriggerNode,
    EVENTLISTENEREMAIL: EmailListenerTriggerNode,

    emailAttributeFilter: EmailAttributeFilterNode,
    EMAIL_ATTRIBUTE_FILTER: EmailAttributeFilterNode,

    emailPatternFilter: EmailPatternFilterNode,
    EMAIL_PATTERN_FILTER: EmailPatternFilterNode,

    attachmentFilter: AttachmentFilterNode,
    ATTACHMENT_FILTER: AttachmentFilterNode,

    attachmentContentProcessor: AttachmentProcessorNode,
    ATTACHMENT_CONTENT_PROCESSOR: AttachmentProcessorNode,

    emailAction: EmailActionNode,
    EMAIL_ACTION: EmailActionNode,
    EMAILACTION: EmailActionNode,

    visual: VisualNode,
    VISUAL_PREVIEW: VisualNode, // Assuming VISUAL_PREVIEW -> VisualNode
    VISUALPREVIEW: VisualNode,

    universalApi: UniversalApiNode,
    UNIVERSAL_API: UniversalApiNode,
    API_CALL: UniversalApiNode,
    APICALL: UniversalApiNode,

    aiAgent: AIAgentNode,
    AI_AGENT: AIAgentNode,

    flowRunner: FlowRunnerNode,
    FLOW_RUNNER: FlowRunnerNode,
    FLOWRUNNER: FlowRunnerNode,

    forEach: ForEachNode,
    FOR_EACH: ForEachNode,
    FOREACH: ForEachNode,

    barrier: BarrierNode,
    BARRIER: BarrierNode,

    multiJoin: MultiJoinNode,
    MULTI_JOIN: MultiJoinNode,
    MULTIJOIN: MultiJoinNode,

    teams: TeamsNode,
    TEAMS_NOTIFY: TeamsNode,
    TEAMS: TeamsNode,

    userAction: UserActionNode,
    USER_ACTION: UserActionNode,
    USERACTION: UserActionNode,

    webhook: WebhookNode,
    WEBHOOK_TRIGGER: WebhookNode,
    WEBHOOK: WebhookNode,

    generateDocument: GenerateDocumentNode,
    GENERATE_DOCUMENT: GenerateDocumentNode,
    GENERATEDOCUMENT: GenerateDocumentNode,

    // Data Model
    dataModel: DataModelNode,
    DATA_MODEL: DataModelNode,
    DATA_MODEL_OUTPUT: OutputNode,

    schedule: ScheduleNode,
    SCHEDULE: ScheduleNode,

    // Workflow Automation
    fileListener: FileListenerNode,
    FILE_LISTENER: FileListenerNode,
    documentParser: DocumentParserNode,
    DOCUMENT_PARSER: DocumentParserNode,
    conditionalRouter: ConditionalRouterNode,
    CONDITIONAL_ROUTER: ConditionalRouterNode,
    dataMapper: DataMapperNode,
    DATA_MAPPER: DataMapperNode,
    documentGenerator: DocumentGeneratorNode,
    DOCUMENT_GENERATOR: DocumentGeneratorNode,
    duplicateDetector: DuplicateDetectorNode,
    DUPLICATE_DETECTOR: DuplicateDetectorNode,
    pythonScript: PythonScriptNode,
    PYTHON_SCRIPT: PythonScriptNode,
    loop: LoopNode,
    LOOP: LoopNode,
    contentMerger: ContentMergerNode,
    CONTENT_MERGER: ContentMergerNode,
    aiSummarizer: AISummarizerNode,
    AI_SUMMARIZER: AISummarizerNode,
    databaseLookup: DatabaseLookupNode,
    DATABASE_LOOKUP: DatabaseLookupNode,
    templateRenderer: TemplateRendererNode,
    TEMPLATE_RENDERER: TemplateRendererNode,
    fileOutput: FileOutputNode,
    FILE_OUTPUT: FileOutputNode,
    sharepointAccess: SharePointAccessNode,
    SHAREPOINT_ACCESS: SharePointAccessNode,
    sharepointSearch: SharePointSearchNode,
    SHAREPOINT_SEARCH: SharePointSearchNode,

    // Hybrid Agent (on-premise ETL)
    relaySource: RelaySourceNode,
    RELAY_SOURCE: RelaySourceNode,
    relay_source: RelaySourceNode,

    relaySink: RelaySinkNode,
    RELAY_SINK: RelaySinkNode,
    relay_sink: RelaySinkNode,

    // User Input — runtime parameter collection
    userInput: UserInputNode,
    USER_INPUT: UserInputNode,
    user_input: UserInputNode,

    // File Source — drag-and-drop CSV/Excel/JSON/Parquet onto canvas
    fileSource: FileSourceNode,
    FILE_SOURCE: FileSourceNode,
    file_source: FileSourceNode,

    // File Object — binary file (PDF/DOCX/images) for GenAI/ML/DocumentParser
    fileObject: FileObjectNode,
    FILE_OBJECT: FileObjectNode,

    // HubSpot CRM node
    hubspot: HubSpotNode,
    HUBSPOT: HubSpotNode,

    // Hybrid File Handler
    HYBRID_FILE_HANDLER:  HybridFileHandlerNode,
    hybridFileHandler:    HybridFileHandlerNode,
    hybrid_file_handler:  HybridFileHandlerNode,
};


const CONTAINER_TYPES = ['container', 'CONTAINER', 'forEach', 'FOR_EACH', 'FOREACH', 'loop', 'LOOP'];



export default function FlowCanvas({ onSelectionChange, nodes, edges, onNodesChange, onEdgesChange, onConnect, setNodes: propsSetNodes, setEdges: propsSetEdges, onPaneClick: onPaneClickProp, onRunNode, readOnly = false, onLockToggle, isTogglingLock = false }: FlowCanvasProps) {
    const reactFlowWrapper = useRef<HTMLDivElement>(null);
    const { project } = useReactFlow();

    const memoizedNodeTypes = React.useMemo(() => nodeTypes, []);

    const onDragOver = useCallback((event: React.DragEvent) => {
        event.preventDefault();
        event.dataTransfer.dropEffect = 'move';
    }, []);

    // Helper to find intersecting container
    const isContainerType = (type?: string) => ['container', 'loop', 'forEach', 'LOOP', 'FOR_EACH', 'FOREACH'].includes(type || '');

    const getIntersectingContainer = (node: Node, allNodes: Node[]) => {
        return allNodes.find(n =>
            isContainerType(n.type) &&
            n.id !== node.id &&
            !n.data.isDisabled &&
            node.position.x > n.position.x &&
            node.position.y > n.position.y &&
            node.position.x + (node.width || (node.style?.width as number) || 200) < n.position.x + (n.width || (n.style?.width as number) || 400) &&
            node.position.y + (node.height || (node.style?.height as number) || 64) < n.position.y + (n.height || (n.style?.height as number) || 400)
        );
    };


    const [menu, setMenu] = useState<{ id: string | null; x: number; y: number; type: 'group' | 'container' | 'node' | null }>({ id: null, x: 0, y: 0, type: null });

    const onNodeDragStop = useCallback(
        (event: React.MouseEvent, node: Node) => {
            // Guard against undefined node/position
            if (!node || !node.position) return;

            // NOTE: Smart handle re-routing on drag has been intentionally removed.
            // Reassigning sourceHandle/targetHandle IDs after draw caused spaghetti lines
            // because the stale `edges` closure overwrote state and multiple drag events
            // accumulated conflicting assignments. ReactFlow's native bezier curves
            // adjust correctly as nodes move -- handles are fixed at edge-draw time.

            // --- 2. CONTAINER / GROUPING LOGIC ---
            // Calculate Absolute Position
            let absX = node.position.x;
            let absY = node.position.y;

            if (node.parentNode || node.parentId) {
                const parentId = node.parentNode || node.parentId;
                const parent = nodes.find(n => n.id === parentId);
                if (parent) {
                    absX += parent.position.x;
                    absY += parent.position.y;
                }
            }

            // Find Target Container (Check Top-Left corner for simplicity)
            const targetContainer = nodes.find(n =>
                isContainerType(n.type) &&
                n.id !== node.id &&
                !n.data.isDisabled &&
                absX > n.position.x &&
                absY > n.position.y &&
                absX < n.position.x + (n.width || (n.style?.width as number) || 400) &&
                absY < n.position.y + (n.height || (n.style?.height as number) || 400)
            );

            if (targetContainer) {
                // Moving into (or staying in) container
                if ((node as any).parentNode === targetContainer.id || (node as any).parentId === targetContainer.id) return; // No change

                // Reparent
                propsSetNodes((nds: Node[]) => nds.map(n => {
                    // Update Child
                    if (n.id === node.id) {
                        console.log(`[Loop Debug] Node '${node.id}' reparented to '${targetContainer.id}'`);
                        return {
                            ...n,
                            parentNode: targetContainer.id,
                            parentId: targetContainer.id,
                            extent: 'parent',
                            style: {
                                ...n.style,
                                width: n.style?.width || 200,
                                height: n.style?.height || 64
                            },
                            position: {
                                x: absX - targetContainer.position.x,
                                y: absY - targetContainer.position.y
                            }
                        };
                    }
                    // Update Container (hasChildren = true)
                    if (n.id === targetContainer.id) {
                        return { ...n, data: { ...n.data, hasChildren: true } };
                    }
                    return n;
                }));

                if (targetContainer.type?.toLowerCase() === 'loop') {
                    // Logic for internal loop visuals removed per User Request (Cleanup)
                } else if (node.parentNode || node.parentId) {
                    const oldParentId = node.parentNode || node.parentId;
                    const oldParentType = nodes.find(n => n.id === oldParentId)?.type?.toLowerCase();
                    if (oldParentType === 'loop') {
                        propsSetEdges((eds: Edge[]) => eds.filter(e => e.id !== `loop-edge-${oldParentId}-${node.id}`));
                    }
                }
            } else if (node.parentNode || node.parentId) {
                // Moving out of container (Ungroup)
                const oldParentId = node.parentNode || node.parentId;
                const oldParentType = nodes.find(n => n.id === oldParentId)?.type?.toLowerCase();

                propsSetNodes((nds: Node[]) => {
                    const updatedNodes = nds.map(n => {
                        if (n.id === node.id) {
                            console.log(`[Loop Debug] Node '${node.id}' removed from parent '${oldParentId}'`);
                            const { parentNode, parentId, extent, ...rest } = n as any;
                            return {
                                ...rest,
                                position: { x: absX, y: absY }
                            };
                        }
                        return n;
                    });

                    // Check if old parent is now empty
                    const remainingChildren = updatedNodes.filter(n => n.parentNode === oldParentId);
                    if (remainingChildren.length === 0) {
                        return updatedNodes.map(n => n.id === oldParentId ? { ...n, data: { ...n.data, hasChildren: false } } : n);
                    }
                    return updatedNodes;
                });

                // Remove Visual Loop Edge if moving out of Loop
                if (oldParentType === 'loop') {
                    propsSetEdges((eds: Edge[]) => eds.filter(e => e.id !== `loop-edge-${oldParentId}-${node.id}`));
                }
            }
        },
        [nodes, edges, propsSetNodes, propsSetEdges]
    );

    const onDrop = useCallback(
        async (event: React.DragEvent) => {
            event.preventDefault();

            const reactFlowBounds = reactFlowWrapper.current?.getBoundingClientRect();
            if (!reactFlowBounds) return;

            const position = project({
                x: event.clientX - reactFlowBounds.left,
                y: event.clientY - reactFlowBounds.top,
            });

            // -- Handle File Drop (tabular OR binary file objects) --
            if (event.dataTransfer.files && event.dataTransfer.files.length > 0) {
                const file = event.dataTransfer.files[0];
                const ext = (file.name.split('.').pop() || '').toLowerCase();

                // Tabular types  ->  FILE_SOURCE node (existing pathway)
                const TABULAR_EXTS = new Set(['csv', 'xlsx', 'xls', 'json', 'parquet']);
                // Binary AI/ML  ->  FILE_OBJECT node (new pathway)
                const BINARY_EXTS = new Set(['pdf', 'docx', 'doc', 'png', 'jpg', 'jpeg', 'gif', 'webp', 'tiff', 'tif']);

                if (TABULAR_EXTS.has(ext)) {
                    const token = getStoredToken();
                    if (!token) { notify.warning('You must be logged in to upload files.'); return; }

                    const placeholderId = `file-src-${crypto.randomUUID()}`;
                    const placeholderNode: Node = {
                        id: placeholderId,
                        type: 'fileSource',
                        position,
                        data: { label: file.name, fileName: file.name, fileType: ext, connectionMode: 'file', _uploading: true },
                    };
                    propsSetNodes((nds: Node[]) => [...nds, placeholderNode]);

                    const formData = new FormData();
                    formData.append('file', file);
                    try {
                        const result = await apiFetch('/flows/ops/upload', token, { method: 'POST', body: formData });
                        propsSetNodes((nds: Node[]) => nds.map((n: Node) =>
                            n.id === placeholderId
                                ? { ...n, data: { ...n.data, filePath: result.filePath, fileName: result.fileName ?? file.name, fileType: result.fileType ?? ext, rowCount: result.rowCount ?? 0, columns: result.columns ?? [], preview: result.preview ?? [], connectionMode: 'file', label: result.fileName ?? file.name, _uploading: false } }
                                : n
                        ));
                    } catch (uploadErr: any) {
                        console.error('[FlowCanvas] File upload failed:', uploadErr);
                        propsSetNodes((nds: Node[]) => nds.filter((n: Node) => n.id !== placeholderId));
                        notify.error(`File upload failed: ${uploadErr?.message || 'Unknown error'}`);
                    }
                    return;
                }

                if (BINARY_EXTS.has(ext)) {
                    const token = getStoredToken();
                    if (!token) { notify.warning('You must be logged in to upload files.'); return; }

                    const placeholderId = `file-obj-${crypto.randomUUID()}`;
                    const placeholderNode: Node = {
                        id: placeholderId,
                        type: 'fileObject',
                        position,
                        data: { label: file.name, fileName: file.name, _uploading: true },
                    };
                    propsSetNodes((nds: Node[]) => [...nds, placeholderNode]);

                    const formData = new FormData();
                    formData.append('file', file);
                    try {
                        const result = await apiFetch('/flows/ops/upload-object', token, { method: 'POST', body: formData });
                        propsSetNodes((nds: Node[]) => nds.map((n: Node) =>
                            n.id === placeholderId
                                ? { ...n, data: { ...n.data, filePath: result.filePath, fileName: result.fileName ?? file.name, mimeType: result.mimeType ?? '', sizeBytes: result.sizeBytes ?? 0, label: result.fileName ?? file.name, _uploading: false } }
                                : n
                        ));
                    } catch (uploadErr: any) {
                        console.error('[FlowCanvas] File object upload failed:', uploadErr);
                        propsSetNodes((nds: Node[]) => nds.filter((n: Node) => n.id !== placeholderId));
                        notify.error(`File upload failed: ${uploadErr?.message || 'Unknown error'}`);
                    }
                    return;
                }
            }


            // Handle Standard Node Drag
            const type = event.dataTransfer.getData('application/reactflow/type');
            const label = event.dataTransfer.getData('application/reactflow/label');

            if (typeof type === 'undefined' || !type) {
                return;
            }

            const isNewNodeContainer = isContainerType(type);

            const newNode: Node = {
                id: Math.random().toString(36).substr(2, 9),
                type,
                position,
                selected: true, // Auto-select
                data: { label: label || `${type} node` },
                style: isNewNodeContainer ? { zIndex: -1 } : undefined,
            };

            // Check intersection with Container for auto-parenting (New Node)
            const container = nodes.find(n =>
                isContainerType(n.type) &&
                !n.data.isDisabled &&
                position.x > n.position.x &&
                position.y > n.position.y &&
                position.x < n.position.x + (n.width || 300) &&
                position.y < n.position.y + (n.height || 300)
            );

            if (container) {
                newNode.parentNode = container.id;
                (newNode as any).parentId = container.id;
                newNode.extent = 'parent';
                newNode.position = {
                    x: position.x - container.position.x,
                    y: position.y - container.position.y
                };

                propsSetNodes((nds: Node[]) => {
                    const updated = nds.map((n): Node => {
                        if (n.id === container.id) return { ...n, data: { ...n.data, hasChildren: true } };
                        return { ...n, selected: false };
                    }).concat(newNode);
                    console.log(`[Loop Debug] Node '${newNode.id}' dropped into container '${container.id}' (parentNode set)`);
                    return updated;
                });

                if (container.type?.toLowerCase() === 'loop') {
                    // Internal loop visuals removed per User Request (Cleanup)
                }
            } else {
                propsSetNodes((nds: Node[]) => nds.map((n): Node => ({ ...n, selected: false })).concat(newNode));
            }
        },
        [project, propsSetNodes, nodes], // Added nodes dependency
    );

    useOnSelectionChange({
        onChange: ({ nodes }) => {
            if (onSelectionChange) {
                // Pass the first selected node or null
                onSelectionChange(nodes.length > 0 ? nodes[0] : null);
            }
        },
    });

    const onNodeContextMenu = useCallback(
        (event: React.MouseEvent, node: Node) => {
            event.preventDefault();
            const rect = reactFlowWrapper.current?.getBoundingClientRect();
            setMenu({
                id: node.id,
                x: event.clientX - (rect?.left || 0),
                y: event.clientY - (rect?.top || 0),
                type: isContainerType(node.type) ? 'container' : 'node'
            });
        },
        []
    );

    const onPaneContextMenu = useCallback(
        (event: React.MouseEvent) => {
            event.preventDefault();
            const selectedNodes = nodes.filter(n => n.selected && !isContainerType(n.type));
            if (selectedNodes.length > 0) {
                const rect = reactFlowWrapper.current?.getBoundingClientRect();
                setMenu({
                    id: null,
                    x: event.clientX - (rect?.left || 0),
                    y: event.clientY - (rect?.top || 0),
                    type: 'group'
                });
                return;
            }
            setMenu({ id: null, x: 0, y: 0, type: null });
        },
        [nodes]
    );

    const onPaneClick = useCallback(() => {
        setMenu({ id: null, x: 0, y: 0, type: null });
        onPaneClickProp?.();
    }, [onPaneClickProp]);

    const onNodesDelete = useCallback((deleted: Node[]) => {
        const deletedContainerIds = deleted.filter(n => isContainerType(n.type)).map(n => n.id);
        if (deletedContainerIds.length > 0) {
            propsSetNodes((nds: Node[]) => {
                return nds.filter(n => !n.parentNode || !deletedContainerIds.includes(n.parentNode));
            });
        }
    }, [propsSetNodes]);

    const handleMenuAction = (action: string) => {
        if (menu.type === 'node' && action === 'runNode') {
            // Run this specific node (and its upstream dependencies)
            if (menu.id && onRunNode) {
                onRunNode(menu.id);
            }
        }
        else if (menu.type === 'node' && action === 'removeFromContainer') {
            const nodeId = menu.id!;
            const childNode = nodes.find(n => n.id === nodeId);
            if (childNode && childNode.parentNode) {
                const container = nodes.find(n => n.id === childNode.parentNode);
                if (container) {
                    propsSetNodes((nds: Node[]) =>
                        nds.map(n => {
                            if (n.id === nodeId) {
                                return {
                                    ...n,
                                    parentNode: undefined,
                                    extent: undefined,
                                    position: {
                                        x: container.position.x + n.position.x,
                                        y: container.position.y + n.position.y
                                    }
                                };
                            }
                            return n;
                        })
                    );
                }
            }
        }
        else if (menu.type === 'group' && action === 'group') {
            // ... Grouping Logic
            const selectedNodes = nodes.filter(n => n.selected && !isContainerType(n.type));
            // Calculate Bounding Box
            let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
            selectedNodes.forEach(n => {
                minX = Math.min(minX, n.position.x);
                minY = Math.min(minY, n.position.y);
                const w = n.width || 150;
                const h = n.height || 40;
                maxX = Math.max(maxX, n.position.x + w);
                maxY = Math.max(maxY, n.position.y + h);
            });

            // Padding
            const PADDING = 50;
            const containerX = minX - PADDING;
            const containerY = minY - PADDING;
            const containerW = (maxX - minX) + (PADDING * 2);
            const containerH = (maxY - minY) + (PADDING * 2);

            const containerId = Math.random().toString(36).substr(2, 9);
            const newContainer: Node = {
                id: containerId,
                type: 'container',
                position: { x: containerX, y: containerY },
                style: { width: containerW, height: containerH, zIndex: -1 },
                data: { label: 'New Group', hasChildren: true }, // Set hasChildren!
                selected: true
            };

            propsSetNodes((nds: Node[]) => {
                const rest = nds.filter(n => !selectedNodes.find(sn => sn.id === n.id));
                const updatedSelected = selectedNodes.map(n => ({
                    ...n,
                    parentNode: containerId,
                    parentId: containerId,
                    extent: 'parent',
                    style: {
                        ...n.style,
                        width: n.style?.width || 200,
                        height: n.style?.height || 64
                    },
                    position: {
                        x: n.position.x - containerX,
                        y: n.position.y - containerY
                    },
                    selected: false
                }));
                return [...rest, newContainer, ...updatedSelected];
            });
        }
        else if (CONTAINER_TYPES.includes(menu.type || '')) {
            if (action === 'ungroup') {
                const containerId = menu.id!;
                const container = nodes.find(n => n.id === containerId);
                if (!container) return;

                propsSetNodes((nds: Node[]) => {
                    // Children
                    const children = nds.filter(n => n.parentNode === containerId || (n as any).parentId === containerId);
                    const updatedChildren = children.map(child => ({
                        ...child,
                        parentNode: undefined,
                        extent: undefined,
                        position: {
                            x: container.position.x + child.position.x,
                            y: container.position.y + child.position.y
                        }
                    }));

                    // Remove Container, Keep Others + Updated Children
                    return nds.filter(n => n.id !== containerId && n.parentNode !== containerId).concat(updatedChildren);
                });

                // Remove Loop Visual Edges
                if (container.type?.toLowerCase() === 'loop') {
                    propsSetEdges((eds: Edge[]) => eds.filter(e => !(e.source === containerId && (e.data as any)?.isLoopEdge)));
                }
            } else if (action === 'delete') { // Delete All
                const containerId = menu.id!;
                propsSetNodes((nds: Node[]) => nds.filter(n => n.id !== containerId && n.parentNode !== containerId));
            }
        }
        setMenu({ id: null, x: 0, y: 0, type: null });
    };

    return (
        <div className="flex-1 relative h-full bg-slate-50">
            {!readOnly && <AICommandBar onApply={() => { }} />}
            <div className="absolute inset-0" ref={reactFlowWrapper} onClick={onPaneClick}>
                <ReactFlow
                    nodes={nodes}
                    edges={edges}
                    onNodesChange={onNodesChange}
                    onEdgesChange={onEdgesChange}
                    onConnect={readOnly ? undefined : onConnect}
                    connectionMode={"loose" as any}
                    nodeTypes={memoizedNodeTypes}
                    onDragOver={readOnly ? undefined : onDragOver}
                    onDrop={readOnly ? undefined : onDrop}
                    onNodeDragStop={readOnly ? undefined : onNodeDragStop}
                    onNodesDelete={readOnly ? undefined : onNodesDelete}
                    onNodeContextMenu={readOnly ? undefined : onNodeContextMenu}
                    onPaneContextMenu={readOnly ? undefined : onPaneContextMenu}
                    nodesDraggable={!readOnly}
                    nodesConnectable={!readOnly}
                    elementsSelectable={true}
                    deleteKeyCode={readOnly ? null : ['Backspace', 'Delete']}
                    fitView
                    fitViewOptions={{ padding: 0.3 }}
                    snapToGrid={!readOnly}
                    snapGrid={[15, 15]}
                    onPaneClick={onPaneClick}
                    proOptions={{ hideAttribution: true }}
                    elevateEdgesOnSelect={true}
                >
                    <Controls className="bg-white border border-gray-100 shadow-sm text-gray-500 !m-2" />
                </ReactFlow>

                {/* Canvas lock button — always visible, shows lock state and allows toggling */}
                <button
                    onClick={onLockToggle}
                    disabled={isTogglingLock || !onLockToggle}
                    title={readOnly ? 'Flow is Published (locked). Click to Unpublish & Edit.' : 'Flow is editable. Click to Publish.'}
                    className={`absolute bottom-4 right-4 z-10 flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-semibold shadow-md border transition-all duration-200 select-none
                        ${readOnly
                            ? 'bg-amber-50 border-amber-300 text-amber-700 hover:bg-amber-100'
                            : 'bg-white border-gray-200 text-gray-500 hover:bg-gray-50 hover:text-gray-700'
                        } ${isTogglingLock ? 'opacity-70 cursor-not-allowed' : 'cursor-pointer'}`}
                >
                    {isTogglingLock ? (
                        /* Simple spinner */
                        <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                        </svg>
                    ) : readOnly ? (
                        /* Locked padlock */
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                        </svg>
                    ) : (
                        /* Unlocked padlock */
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                d="M8 11V7a4 4 0 118 0m-4 8v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2z" />
                        </svg>
                    )}
                    <span>{readOnly ? 'Published — Unlock to Edit' : 'Draft — Click to Publish'}</span>
                </button>

                {/* Custom Context Menu */}
                {menu.type && (
                    <div
                        className="absolute z-50 bg-white shadow-lg border border-gray-200 rounded-lg py-1 min-w-[150px]"
                        style={{ top: menu.y, left: menu.x }}
                    >
                        {menu.type === 'node' && (() => {
                            const targetNode = nodes.find(n => n.id === menu.id);
                            return (
                                <>
                                    <button
                                        className="w-full text-left px-4 py-2 hover:bg-gray-50 text-sm flex items-center gap-2"
                                        onClick={() => handleMenuAction('runNode')}
                                    >
                                        <Play className="w-4 h-4 text-green-600" /> Run Node
                                    </button>
                                    {targetNode?.parentNode && (
                                        <button
                                            className="w-full text-left px-4 py-2 hover:bg-gray-50 text-sm flex items-center gap-2"
                                            onClick={() => handleMenuAction('removeFromContainer')}
                                        >
                                            <LogOut className="w-4 h-4 text-gray-600" /> Remove from Container
                                        </button>
                                    )}
                                </>
                            );
                        })()}
                        {menu.type === 'group' && (
                            <button
                                className="w-full text-left px-4 py-2 hover:bg-gray-50 text-sm flex items-center gap-2"
                                onClick={() => handleMenuAction('group')}
                            >
                                <Box className="w-4 h-4" /> Group Selection
                            </button>
                        )}
                        {CONTAINER_TYPES.includes(menu.type || '') && (
                            <>
                                <button
                                    className="w-full text-left px-4 py-2 hover:bg-gray-50 text-sm flex items-center gap-2 text-red-600"
                                    onClick={() => handleMenuAction('delete')}
                                >
                                    <div className="w-4 h-4 border-2 border-red-500 rounded-sm" /> Delete Container & Nodes
                                </button>
                                <div className="border-t my-1" />
                                <div className="px-4 py-1 text-xs text-gray-400 font-semibold">OPTIONS</div>
                                <button
                                    className="w-full text-left px-4 py-2 hover:bg-gray-50 text-sm flex items-center gap-2"
                                    onClick={() => handleMenuAction('ungroup')}
                                >
                                    <Layers className="w-4 h-4" /> Remove Container (Keep Nodes)
                                </button>
                            </>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}